import { Button, Loader, Text, Title } from '@mantine/core';
import { storage } from '@newo/storage';
import { Building2 } from 'lucide-react';
import { type FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CreatorFlowLayout from '@/components/CreatorFlowLayout';
import { PATHS } from '@/constants';
import { useHostContext } from '@/context/HostContext.tsx';
import { buildNavigatePath } from '@/routes';
import useAppStore from '@/store/app';
import { useInitializeVibeCreator } from '@/utils/api';
import { LookupResultKind } from './BusinessConfirmation.types';
import { getBusinessConfirmationFlowSteps } from './businessConfirmationFlowSteps';
import MapsCard from './components/MapsCard';
import SearchAgainForm from './components/SearchAgainForm';
import { useBusinessLookupMutation } from './hooks/useBusinessLookup';

const BusinessConfirmation: FC = () => {
	const navigate = useNavigate();
	const { localization } = useHostContext();
	const source = useAppStore((state) => state.businessSource) ?? '';
	const { mutateAsync: initVibeCreator } = useInitializeVibeCreator();

	const handleConfirm = useCallback(async () => {
		storage.clear();
		const { externalId } = await initVibeCreator();

		const basePath = buildNavigatePath(PATHS.creatorVoice);
		const searchParams = new URLSearchParams();
		if (source) {
			searchParams.set('description', source);
		}
		if (externalId) {
			searchParams.set('actorExternalId', externalId);
		}
		const query = searchParams.toString();
		navigate(query ? `${basePath}?${query}` : basePath);
	}, [initVibeCreator, navigate, source]);

	const template = `${source}\n- Name:\n- Address:\n- Type of business:\n- Working hours:\n- Main services and prices:`;
	const [searchQuery, setSearchQuery] = useState(source);
	const [formValue, setFormValue] = useState(template);
	const [showSearchInput, setShowSearchInput] = useState(false);
	const { data: result, mutate: lookup } = useBusinessLookupMutation();
	const flowSteps = getBusinessConfirmationFlowSteps();

	useEffect(() => {
		lookup(searchQuery);
	}, [searchQuery, lookup]);

	if (showSearchInput) {
		return (
			<CreatorFlowLayout steps={flowSteps}>
				<SearchAgainForm
					initialValue={formValue}
					onSearch={(query) => {
						setFormValue(query);
						setSearchQuery(query);
						setShowSearchInput(false);
					}}
				/>
			</CreatorFlowLayout>
		);
	}

	if (!result) {
		return (
			<CreatorFlowLayout steps={flowSteps}>
				<div
					className="flex min-h-[360px] flex-1 flex-col items-center justify-center gap-3"
					data-testid="business-confirmation-loading"
				>
					<Loader size="lg" />
					<Text
						c="dimmed"
						size="sm"
					>
						{localization.translate('creator', 'businessConfirmation.loading')}
					</Text>
				</div>
			</CreatorFlowLayout>
		);
	}

	if (result.kind === LookupResultKind.NOTFOUND) {
		return (
			<CreatorFlowLayout steps={flowSteps}>
				<div
					className="flex min-h-[360px] flex-1 flex-col items-center justify-center gap-6 p-8 text-center"
					data-testid="business-confirmation-not-found"
				>
					<Building2 className="h-12 w-12 text-slate-300" />
					<Title order={3}>
						{localization.translate(
							'creator',
							'businessConfirmation.notFound.title'
						)}
					</Title>
					<Text
						c="dimmed"
						size="sm"
					>
						{searchQuery}
					</Text>
					<div className="flex flex-wrap justify-center gap-3">
						<Button
							className="hover:!bg-transparent"
							data-testid="business-confirmation-try-again"
							onClick={() => setShowSearchInput(true)}
							variant="outline"
						>
							{localization.translate(
								'creator',
								'businessConfirmation.notFound.tryAgainButton'
							)}
						</Button>
						<Button
							className="hover:!bg-transparent"
							data-testid="business-confirmation-confirm-anyway"
							onClick={handleConfirm}
							variant="outline"
						>
							{localization.translate(
								'creator',
								'businessConfirmation.notFound.continueButton'
							)}
						</Button>
					</div>
				</div>
			</CreatorFlowLayout>
		);
	}

	return (
		<CreatorFlowLayout steps={flowSteps}>
			<MapsCard
				onConfirm={handleConfirm}
				onNo={() => setShowSearchInput(true)}
				profile={result.profile}
			/>
		</CreatorFlowLayout>
	);
};

export default BusinessConfirmation;
